import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import '../../styles/AuthForm.css';
import { useAuth } from '../../auth/useAuth';
import SigningIn from './SigningIn';
import API_BASE from '../../utils/api.js';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  const navigate = useNavigate();
  const { auth, login } = useAuth();

  /* ------------------------------------------------
     LOGIN SUBMIT
  ------------------------------------------------ */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSigningIn(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const text = await res.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error('Response is not valid JSON');
        setLoginError('We could not sign you in right now. Please try again.');
        setIsSigningIn(false);
        return;
      }

      if (!res.ok) {
        setIsSigningIn(false);
        setLoginError(
          data?.error?.toLowerCase().includes('incorrect')
            ? 'Incorrect email or password. Please try again.'
            : data?.error || 'Login failed. Please try again.'
        );
        return;
      }

      const { accessToken, idToken, refreshToken, expiresIn } = data;
      const decoded = jwtDecode(idToken);
      const userId = decoded.sub;
      const userEmail = decoded.email;

      localStorage.setItem('userId', userId);
      if (userEmail) {
        localStorage.setItem('userEmail', userEmail);
      }

      const onboardingStatus = await login({ accessToken, idToken, refreshToken, expiresIn });

      /* --------------------------------------------
         ROUTING DECISION
      -------------------------------------------- */
      switch (onboardingStatus) {
        case 'pending':
          navigate('/plans');
          break;
        case 'demo_requested':
          navigate('/demo');
          break;
        case 'plan_selected':
          navigate('/checkout');
          break;
        case 'paid':
          navigate('/dashboard');
          break;
        default:
          navigate('/plans');
      }
    } catch (err) {
      console.error(err);
      setIsSigningIn(false);
      setLoginError('Something went wrong. Please try again.');
    }
  };

  if (isSigningIn) {
    return <SigningIn email={email} />;
  }

  // Opening the app at "/" with a live (or refreshable) session should land in
  // the app, not on a login form that makes it look like the user was signed out.
  if (!auth.isHydrated) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (auth.isLoggedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="auth-form">
      <img src="/logo.png" alt="InteriorFolio Logo" className="auth-logo" />

      <form className="auth-card" onSubmit={handleLogin}>
        <h2>Log In</h2>

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (loginError) setLoginError('');
          }}
          aria-invalid={Boolean(loginError)}
          required
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (loginError) setLoginError('');
          }}
          aria-invalid={Boolean(loginError)}
          required
        />

        {loginError ? (
          <div className="auth-error-banner" role="alert" aria-live="polite">
            <strong>Unable to sign in.</strong> {loginError}
          </div>
        ) : null}

        <p className="auth-secondary-link">
          <Link to="/forgot-password">Forgot Password?</Link>
        </p>

        <button type="submit">Log In</button>

        <p>
          Don&apos;t have an account? <Link to="/signup">Sign up</Link>
        </p>
      </form>

      <p style={{ marginTop: '1.25rem', fontSize: '0.78rem', color: '#9ca3af', textAlign: 'center' }}>
        By signing in, you agree to our{' '}
        <Link to="/terms" style={{ color: '#6b7280', textDecoration: 'underline' }}>Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" style={{ color: '#6b7280', textDecoration: 'underline' }}>Privacy Policy</Link>
      </p>

      <p style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#9ca3af', textAlign: 'center' }}>
        Are you a client?{' '}
        <Link to="/client/login" style={{ color: '#62794e', fontWeight: 600, textDecoration: 'none' }}>
          Access the Client Portal →
        </Link>
      </p>
    </div>
  );
}

export default Login;
